import { create } from "zustand";
import type { PanelSide } from "components/canvas/panelRoom";
import type { ClientApp } from "target_wasm";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import {
  can_import_from_connections,
  can_import_into,
} from "@/components/canvas/utils/roles";
import { ALL_SOURCES, ImportSourceType } from "./import/ImportSourceType";

export enum ETabItem {
  Directory = "Directory",
  Import = "Import",
}

/** Where this client may import from, or nowhere. The tab, its source row and
 *  the bar's Import all read this. */
export function importSources(
  clientApp: ClientApp,
  scheduled: boolean,
): ImportSourceType[] {
  const role = clientApp.user_role();
  if (scheduled || !can_import_into(role)) return [];
  // A connector table is its owner's to import and sync, and never enters the
  // catalog: its data stays with the credentials that connected it.
  const isCatalog = clientApp.peek_model_permissions()?.is_catalog ?? false;
  return isCatalog || !can_import_from_connections(role)
    ? ALL_SOURCES.filter((s) => s !== ImportSourceType.Connections)
    : ALL_SOURCES;
}

interface ModelPanel {
  tab: ETabItem;
  source: ImportSourceType;
}

const NEW_MODEL_PANEL = {
  tab: ETabItem.Directory,
  source: ImportSourceType.Mycorr,
};

interface LeftPanel {
  /** The model on the canvas: the panel's actions apply to its entry. */
  modelId: string | null;
  /** Each model reopens on the tab and source it was left on. */
  panels: Record<string, ModelPanel>;
  /** Asked for from elsewhere (⌘F); the directory's search bar takes it. */
  searchPending: boolean;
  /** Folded away by the canvas's left edge; asking for search or import reopens it. */
  collapsed: boolean;
  toggleCollapsed: () => void;
  /** Opens it, as the one to keep where the window holds one panel only. */
  openPanel: () => void;
  /** With room for one side panel, the one asked for last. */
  crowdedKeeps: PanelSide;
  keepDetails: () => void;
  enterModel: (modelId: string) => void;
  showTab: (tab: ETabItem) => void;
  showSource: (source: ImportSourceType) => void;
  /** Refuses a source `importSources` does not list. */
  openImport: (source: ImportSourceType) => void;
  openSearch: () => void;
  takeSearch: () => void;
}

export const panelOf = (
  state: Pick<LeftPanel, "panels">,
  modelId: string | null | undefined,
): ModelPanel => (modelId && state.panels[modelId]) || NEW_MODEL_PANEL;

const withPanel = (s: LeftPanel, change: Partial<ModelPanel>) =>
  s.modelId
    ? {
        panels: {
          ...s.panels,
          [s.modelId]: { ...panelOf(s, s.modelId), ...change },
        },
      }
    : {};

export const useLeftPanelStore = create<LeftPanel>((set) => ({
  modelId: null,
  panels: {},
  searchPending: false,
  collapsed: false,
  toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
  openPanel: () => set({ collapsed: false, crowdedKeeps: "left" }),
  crowdedKeeps: "left",
  keepDetails: () => set({ crowdedKeeps: "details" }),
  enterModel: (modelId) => set({ modelId, searchPending: false }),
  showTab: (tab) => set((s) => withPanel(s, { tab })),
  showSource: (source) => set((s) => withPanel(s, { source })),
  openImport: (source) => {
    const { clientApp, modelScheduledDeletionDate } = useBoundStore.getState();
    const scheduled = (modelScheduledDeletionDate ?? 0) > 0;
    if (clientApp && importSources(clientApp, scheduled).includes(source))
      set((s) => ({
        ...withPanel(s, { tab: ETabItem.Import, source }),
        collapsed: false,
        crowdedKeeps: "left",
      }));
  },
  openSearch: () =>
    set((s) => ({
      ...withPanel(s, { tab: ETabItem.Directory }),
      searchPending: true,
      collapsed: false,
      crowdedKeeps: "left",
    })),
  takeSearch: () => set({ searchPending: false }),
}));
